import { memo } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import {
  AttributionQuotaBase,
  useAttributionQuota,
} from './AttributionQuotaIndicator';
import { ThemeToggle } from './ThemeToggle';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

type NavItem = {
  name: string;
  href: string;
};

type MobileMenuProps = {
  isOpen: boolean;
  navItems: NavItem[];
  activeTab: string;
  setActiveTab: (value: string) => void;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  onLogoutClick: (e: React.MouseEvent) => void;
  onClose: () => void;
};

export const MobileMenu = memo<MobileMenuProps>(
  ({
    isOpen,
    navItems,
    activeTab,
    setActiveTab,
    user,
    onLogoutClick,
    onClose,
  }) => {
    const { data: attributionQuota } = useAttributionQuota();

    if (!isOpen) return null;

    return (
      <div className="lg:hidden">
        <div className="space-y-1 bg-background px-4 pb-3 pt-2">
          {navItems.map((item) => (
            <div key={item.name}>
              {item.href.startsWith('http') ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'block px-3 py-2 text-base font-medium transition-colors hover:text-primary',
                    'text-muted-foreground',
                  )}
                  onClick={onClose}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    'block px-3 py-2 text-base font-medium transition-colors hover:text-primary',
                    activeTab === item.name
                      ? 'text-primary'
                      : 'text-muted-foreground',
                  )}
                  onClick={() => {
                    setActiveTab(item.name);
                    onClose();
                  }}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}

          {user ? (
            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-3">
                <div className="text-base font-medium">{user.firstName}</div>
                <div>{user.email}</div>
                <Separator className="my-2" />
              </div>

              {attributionQuota && (
                <AttributionQuotaBase
                  used={attributionQuota.used}
                  total={attributionQuota.total}
                  percentage={attributionQuota.percentage}
                />
              )}

              <div className="mt-3 flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Theme</span>
                  <ThemeToggle />
                </div>
                <Link
                  to="/account"
                  className="flex items-center py-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={onClose}
                >
                  <User className="mr-2 h-4 w-4" />
                  Account
                </Link>
                <button
                  onClick={(e) => {
                    onClose();
                    onLogoutClick(e);
                  }}
                  className="flex w-full items-center py-2 text-sm text-muted-foreground hover:text-primary"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <>
              <Separator className="my-2" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Theme</span>
                <ThemeToggle />
              </div>
              <div className="text-lg">
                <div className="flex flex-col space-y-3">
                  <Link
                    to="/login"
                    className="py-2 text-muted-foreground hover:text-primary"
                    onClick={onClose}
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="py-2 text-muted-foreground hover:text-primary"
                    onClick={onClose}
                  >
                    Register
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

MobileMenu.displayName = 'MobileMenu';
